import { RefObject, useEffect } from 'react';
import { pointerEngine } from './pointerEngine';
import { playLoop } from './playLoop';
import { stepSpring, settled, SpringPreset } from './springs';
import { RectCache, nearRect } from './rectCache';
import { isProtectedElement } from './protect';
import { useStorm } from '../storm/StormContext';
import { useFpsBackoff } from '../storm/useFpsBackoff';

// Leaning frame: the decorative .gj-frame tilts up to MAX_DEG toward the pointer (relative to
// the section centre). Mouse: while the pointer is over the section. Touch: while pressed in
// it. Written as a CSS variable (--lean) so the storm-warp rule can compose with it.
const MAX_DEG = 4;
const LEAN: SpringPreset = { stiffness: 90, damping: 16 };
const EPS = 0.01;

const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));

export function useLeanFrame(frameRef: RefObject<HTMLElement>, sectionRef: RefObject<HTMLElement>): void {
  const { reducedMotion } = useStorm();
  const q = useFpsBackoff();
  const off = reducedMotion || q >= 2;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !sectionRef.current || off) return;
    if (isProtectedElement(frame)) return;
    const box = new RectCache(() => sectionRef.current);
    let rx = { x: 0, v: 0 }, ry = { x: 0, v: 0 };
    let wx = 0, wy = 0;
    let unreg: (() => void) | null = null;
    let moved = false;

    const target = (r: DOMRect | null) => {
      const p = pointerEngine.state;
      const live = p.type === 'mouse' || p.down;
      if (!r || !live || !nearRect(r, p.x, p.y, 0)) return { tx: 0, ty: 0, inside: false };
      const nx = clamp1((p.x - (r.left + r.width / 2)) / (r.width / 2));
      const ny = clamp1((p.y - (r.top + r.height / 2)) / (r.height / 2));
      return { tx: -ny * MAX_DEG, ty: nx * MAX_DEG, inside: true };
    };
    const write = () => {
      if (Math.abs(rx.x - wx) < EPS && Math.abs(ry.x - wy) < EPS) return;
      wx = rx.x; wy = ry.x;
      if (Math.abs(wx) < EPS && Math.abs(wy) < EPS) { frame.style.removeProperty('--lean'); return; }
      frame.style.setProperty('--lean', `perspective(1200px) rotateX(${wx.toFixed(3)}deg) rotateY(${wy.toFixed(3)}deg)`);
    };
    const tick = (dt: number) => {
      const t = target(box.get(performance.now(), moved));
      moved = false;
      rx = stepSpring(rx, t.tx, dt, LEAN);
      ry = stepSpring(ry, t.ty, dt, LEAN);
      const done = settled(rx, t.tx) && settled(ry, t.ty);
      if (done) { rx = { x: t.tx, v: 0 }; ry = { x: t.ty, v: 0 }; }
      write();
      if (done && unreg) { unreg(); unreg = null; }
    };
    const wake = () => { moved = true; if (!unreg) unreg = playLoop.register(tick); };
    const offs = [
      pointerEngine.on('move', wake), pointerEngine.on('down', wake), pointerEngine.on('up', wake),
    ];
    return () => {
      offs.forEach((o) => o());
      if (unreg) unreg();
      frame.style.removeProperty('--lean');
    };
  }, [frameRef, sectionRef, off]);
}
